import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { InputAdornment, TextField } from '@mui/material'

const currencyConfig = {
  locale: 'pt-BR',
  currency: 'BRL',
}

const formatter = new Intl.NumberFormat(currencyConfig.locale, {
  style: 'currency',
  currency: currencyConfig.currency,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const numberFormatter = new Intl.NumberFormat(currencyConfig.locale, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const parseDigitsToFloat = (digits) => {
  if (typeof digits !== 'string' || !digits) return 0

  if (typeof digits === 'number') return digits

  const cleanedDigits = digits.replace(/[^\d]/g, '')

  if (cleanedDigits.length === 0) return 0

  const num = parseInt(cleanedDigits, 10)

  return num / 100
}

function CurrencyField({
  value,
  onChange,
  label,
  max,
  compactCurrencySymbol = false,
  InputProps,
  inputProps,
  ...props
}) {
  const [digits, setDigits] = useState(() => {
    return value ? Math.round(value * 100).toString() : '0'
  })

  const isTypingRef = useRef(false)

  useEffect(() => {
    if (isTypingRef.current) return
    if (value !== undefined && value !== null) {
      const newDigits = Math.round(value * 100).toString()
      setDigits(newDigits)
    }
  }, [value])

  const handleChange = useCallback(
    (event) => {
      isTypingRef.current = true

      const nativeEvent = event.nativeEvent
      const inputType = nativeEvent?.inputType || ''
      const inputData = nativeEvent?.data

      let newDigits = digits

      if (
        inputType.startsWith('insert') &&
        inputData &&
        /^\d$/.test(inputData)
      ) {
        newDigits = digits === '0' ? inputData : digits + inputData
        if (newDigits.length > 15) {
          newDigits = newDigits.slice(1)
        }
      } else if (inputType.startsWith('delete')) {
        newDigits = digits.slice(0, -1) || '0'
      }

      if (onChange) {
        const floatValue = parseDigitsToFloat(newDigits)
        if (max != null && floatValue > max) return
        onChange(floatValue)
      }
      setDigits(newDigits)
    },
    [digits, onChange, max],
  )

  const handleBlur = useCallback(() => {
    isTypingRef.current = false
    const floatValue = parseDigitsToFloat(digits)
    if (onChange) {
      onChange(floatValue)
    }
  }, [digits, onChange])

  const displayValue = useMemo(() => {
    const floatValue = parseDigitsToFloat(digits)
    return compactCurrencySymbol
      ? numberFormatter.format(floatValue)
      : formatter.format(floatValue)
  }, [compactCurrencySymbol, digits])

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      variant="outlined"
      color="primary"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      type="text"
      InputProps={{
        ...InputProps,
        startAdornment: compactCurrencySymbol ? (
          <InputAdornment position="start" sx={{ fontSize: '0.55rem' }}>
            R$
          </InputAdornment>
        ) : (
          InputProps?.startAdornment
        ),
      }}
      inputProps={{
        ...inputProps,
        inputMode: 'numeric',
        pattern: '[0-9]*',
      }}
    />
  )
}

export default CurrencyField
